import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Select,
  MenuItem,
  TextField
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import InvoiceSearch from "./InvoiceProduct/InvoiceSearch";
import InvoiceProductForm from "./InvoiceProduct/InvoiceProductForm";
import TotalForm from "./InvoiceProduct/TotalForm";

export default function InfoProduct({
  onProductDataChange,
  rows,
  setRows,
  descripcion,
  setDescripcion
}) {
  const searchRef = useRef();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const safeRows = Array.isArray(rows) ? rows : [];

  const normalizeDecimalInput = (raw) => {
    let s = String(raw ?? "");
    s = s.replace(/,/g, ".");
    s = s.replace(/[^\d.]/g, "");
    const firstDot = s.indexOf(".");
    if (firstDot !== -1) {
      s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
    }
    return s;
  };

  const normalizeIntegerInput = (raw) => {
    let s = String(raw ?? "");
    s = s.replace(/[^\d]/g, "");
    return s;
  };

  const toNum = (v) => {
    if (v === "" || v === null || v === undefined) return 0;
    const n = Number(String(v).replace(/,/g, "."));
    return Number.isFinite(n) ? n : 0;
  };

  const centsToNumber = (c) => Number((c / 100).toFixed(2));

  const calcRow = (r) => {
    const qty = r.cantidad === "" ? 0 : toNum(r.cantidad);
    const price = r.pvp === "" ? 0 : toNum(r.pvp);
    const dPct = r.descuentoPct === "" ? 0 : toNum(r.descuentoPct);
    const ivaPct = Number(r.iva_porcentaje ?? 15);

    const brutoC = Math.round(price * qty * 100);
    const descC = Math.round((brutoC * dPct) / 100);
    const netoC = brutoC - descC;

    return {
      qty,
      price,
      dPct,
      ivaPct,
      brutoC,
      descC,
      netoC,
      bruto: centsToNumber(brutoC),
      descTotal: centsToNumber(descC),
      neto: centsToNumber(netoC)
    };
  };

  const totals = useMemo(() => {
    return safeRows.reduce(
      (acc, r) => {
        const { brutoC, descC, netoC, ivaPct } = calcRow(r);

        if (ivaPct === 15) {
          acc.bruto15C += brutoC;
          acc.neto15C += netoC;
        } else if (ivaPct === 5) {
          acc.bruto5C += brutoC;
          acc.neto5C += netoC;
        } else if (ivaPct === 999) {
          acc.brutoNoObjC += brutoC;
          acc.netoNoObjC += netoC;
        } else {
          acc.bruto0C += brutoC;
          acc.neto0C += netoC;
        }

        acc.descuentoC += descC;
        return acc;
      },
      {
        bruto15C: 0,
        bruto5C: 0,
        bruto0C: 0,
        brutoNoObjC: 0,
        neto15C: 0,
        neto5C: 0,
        neto0C: 0,
        netoNoObjC: 0,
        descuentoC: 0
      }
    );
  }, [safeRows]);

  const subtotalSinImpuestosC =
    totals.neto15C + totals.neto5C + totals.neto0C + totals.netoNoObjC;

  const iva15C = Math.round((totals.neto15C * 15) / 100);
  const iva5C = Math.round((totals.neto5C * 5) / 100);

  const iva15 = centsToNumber(iva15C);
  const iva5 = centsToNumber(iva5C);
  const total = centsToNumber(subtotalSinImpuestosC + iva15C + iva5C);

  const productosPayload = useMemo(() => {
    return safeRows.map((r) => ({
      product_id: r.product_id ?? null,
      variant_id: r.variant_id ?? null,
      code: r.code || "",
      sku: r.sku || "",
      variant_type: r.variant_type || "",
      value: r.value || "",
      codigo_principal: r.codigo_principal || "",
      codigo_auxiliar: r.codigo_auxiliar || "",
      descripcion: r.descripcion || "",
      detalles: r.detalles || "",
      cantidad: r.cantidad ?? "",
      pvp: r.pvp ?? "",
      iva_porcentaje: r.iva_porcentaje ?? 0,
      descuentoPct: r.descuentoPct ?? ""
    }));
  }, [safeRows]);

  useEffect(() => {
    onProductDataChange?.({
      productos: productosPayload,
      productos_raw: safeRows,
      subtotal15: centsToNumber(totals.neto15C),
      subtotal5: centsToNumber(totals.neto5C),
      subtotal0: centsToNumber(totals.neto0C),
      subtotalNoObj: centsToNumber(totals.netoNoObjC),
      subtotal15_bruto: centsToNumber(totals.bruto15C),
      subtotal5_bruto: centsToNumber(totals.bruto5C),
      subtotal0_bruto: centsToNumber(totals.bruto0C),
      subtotalNoObj_bruto: centsToNumber(totals.brutoNoObjC),
      subtotal_sin_impuestos: centsToNumber(subtotalSinImpuestosC),
      descuento_total: centsToNumber(totals.descuentoC),
      iva15,
      iva5,
      ice: 0,
      total,
      descripcion: descripcion || "",
      descuentos_ya_aplicados_en_subtotales: true
    });
  }, [
    productosPayload,
    safeRows,
    totals,
    subtotalSinImpuestosC,
    iva15,
    iva5,
    total,
    descripcion,
    onProductDataChange
  ]);

  const handleAdd = ({
    pvp,
    iva_porcentaje,
    detalles,
    descuentoPct,
    codigo_auxiliar
  }) => {
    if (!selectedProduct) {
      setErrorMessage("Debe seleccionar un producto.");
      return;
    }

    const skuFinal = selectedProduct.sku || "";
    const codeFinal = selectedProduct.code || "";
    const codePrincipal = skuFinal ? `${codeFinal}-${skuFinal}` : codeFinal;
    const codeAux = codigo_auxiliar || selectedProduct.codigo_auxiliar || "";
    const baseName = selectedProduct.name || "";
    const pvpNorm = normalizeDecimalInput(pvp);
    const descNorm = normalizeDecimalInput(descuentoPct);

    setRows([
      ...safeRows,
      {
        product_id: selectedProduct.product_id ?? selectedProduct.id ?? null,
        variant_id: selectedProduct.variant_id ?? null,
        code: selectedProduct.code || "",
        sku: selectedProduct.sku || "",
        variant_type: selectedProduct.variant_type || "",
        value: selectedProduct.value || "",
        codigo_principal: codePrincipal,
        codigo_auxiliar: codeAux,
        cantidad: "1",
        descripcion: baseName,
        detalles: detalles || "",
        pvp: pvpNorm,
        iva_porcentaje: Number(iva_porcentaje),
        descuentoPct: descNorm === "" ? "0" : String(Number(descNorm))
      }
    ]);

    setSelectedProduct(null);
    searchRef.current?.clear();
    setErrorMessage("");
  };

  const handleDelete = (idx) => {
    setRows(safeRows.filter((_, i) => i !== idx));
  };

  const handleFieldChange = (idx, field, value) => {
    setRows(
      safeRows.map((r, i) => {
        if (i !== idx) return r;

        if (field === "cantidad") {
          const v = normalizeIntegerInput(value);
          return { ...r, cantidad: v };
        }

        if (field === "descuentoPct") {
          const v = normalizeDecimalInput(value);
          if (v === "" || /^\d*\.?\d*$/.test(v)) return { ...r, descuentoPct: v };
          return r;
        }

        if (field === "pvp") {
          const v = normalizeDecimalInput(value);
          if (v === "" || /^\d*\.?\d*$/.test(v)) return { ...r, pvp: v };
          return r;
        }

        if (field === "iva_porcentaje") return { ...r, iva_porcentaje: Number(value) };

        return { ...r, [field]: value };
      })
    );
  };

  const renderDescripcion = (r) => {
    const desc = (r.descripcion || "").trim();
    const det = (r.detalles || "").trim();
    if (!det) return desc;
    return `${desc}\n- ${det}`;
  };

  const renderCodigo = (r) => {
    const principal = String(r.codigo_principal || "").trim();
    const auxiliar = String(r.codigo_auxiliar || "").trim();
    return auxiliar ? `${principal} / ${auxiliar}` : principal;
  };

  return (
    <Box sx={{ width: "100%" }}>
      {errorMessage && (
        <Typography color="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Typography>
      )}

      <Grid container spacing={2}>
        <InvoiceSearch ref={searchRef} onSelect={setSelectedProduct} />
        <InvoiceProductForm producto={selectedProduct} onAdd={handleAdd} />
      </Grid>

      <Box sx={{ mt: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#1565C0" }}>
              <TableCell sx={{ color: "#fff" }}>Código / Auxiliar</TableCell>
              <TableCell sx={{ color: "#fff" }}>Cant.</TableCell>
              <TableCell sx={{ color: "#fff" }}>Descripción</TableCell>
              <TableCell sx={{ color: "#fff" }}>Precio U.</TableCell>
              <TableCell sx={{ color: "#fff" }}>IVA (%)</TableCell>
              <TableCell sx={{ color: "#fff" }}>Desc. (%)</TableCell>
              <TableCell sx={{ color: "#fff" }}>Desc. ($)</TableCell>
              <TableCell sx={{ color: "#fff" }}>Subtotal</TableCell>
              <TableCell sx={{ color: "#fff" }}>Eliminar</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {safeRows.map((r, i) => {
              const { descTotal, neto } = calcRow(r);

              return (
                <TableRow key={i}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {renderCodigo(r)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <TextField
                      value={r.cantidad}
                      onChange={(e) => handleFieldChange(i, "cantidad", e.target.value)}
                      sx={{ width: 60 }}
                      inputProps={{ inputMode: "numeric" }}
                    />
                  </TableCell>

                  <TableCell sx={{ minWidth: 1 / 0 }}>
                    <TextField
                      value={renderDescripcion(r)}
                      fullWidth
                      multiline
                      minRows={3}
                      maxRows={8}
                      InputProps={{ readOnly: true }}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      value={r.pvp}
                      onChange={(e) => handleFieldChange(i, "pvp", e.target.value)}
                      type="text"
                      sx={{ width: 100 }}
                      inputProps={{ inputMode: "decimal" }}
                    />
                  </TableCell>

                  <TableCell>
                    <Select
                      value={r.iva_porcentaje}
                      onChange={(e) => handleFieldChange(i, "iva_porcentaje", e.target.value)}
                      sx={{ width: 100 }}
                    >
                      <MenuItem value={0}>0%</MenuItem>
                      <MenuItem value={5}>5%</MenuItem>
                      <MenuItem value={15}>15%</MenuItem>
                      <MenuItem value={999}>No objeto</MenuItem>
                    </Select>
                  </TableCell>

                  <TableCell>
                    <TextField
                      value={r.descuentoPct}
                      onChange={(e) => handleFieldChange(i, "descuentoPct", e.target.value)}
                      sx={{ width: 70 }}
                      inputProps={{ inputMode: "decimal" }}
                    />
                  </TableCell>

                  <TableCell>${Number(descTotal || 0).toFixed(2)}</TableCell>
                  <TableCell>${Number(neto || 0).toFixed(2)}</TableCell>

                  <TableCell>
                    <IconButton onClick={() => handleDelete(i)}>
                      <DeleteIcon color="error" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      <TotalForm
        descripcion={descripcion}
        setDescripcion={setDescripcion}
        totals={totals}
        iva15={iva15}
        iva5={iva5}
        total={total}
      />
    </Box>
  );
}